import React, { useState, useRef, useEffect } from 'react';
import { X, Plus, Trash2, Pencil } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Registry, RegistryItem } from '../types/registry';
import DatePicker from './DatePicker';
import GuestListManager from './GuestListManager';
import EditItemModal from './EditItemModal';

interface Props {
  registry: Registry & { items: RegistryItem[] };
  onClose: () => void;
  onUpdate: () => void;
}

export default function EditRegistryModal({ registry, onClose, onUpdate }: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [title, setTitle] = useState(registry.title);
  const [description, setDescription] = useState(registry.description || '');
  const [eventDate, setEventDate] = useState(registry.event_date);
  const [thankYouMessage, setThankYouMessage] = useState(registry.thank_you_message || '');
  const [items, setItems] = useState<RegistryItem[]>(registry.items);
  const [itemsToDelete, setItemsToDelete] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingItem, setEditingItem] = useState<{
    item: RegistryItem | Partial<RegistryItem>;
    index: number | null;
  } | null>(null);

  // Prevent scroll on body when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node) && !editingItem) {
        event.stopPropagation();
      }
    };

    // Handle escape key
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscapeKey, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [loading, onClose]);

  const handleAddNewItem = () => {
    setEditingItem({
      item: {
        name: '',
        price: 0,
        priority: 'medium' as const,
        allows_group_gifting: false,
        url: null,
        target_amount: null
      },
      index: null
    });
  };

  const handleRemoveItem = async (index: number) => {
    const itemToRemove = items[index];
    
    if (itemToRemove.id) {
      // Add to the list of items to delete from the database
      setItemsToDelete(prev => [...prev, itemToRemove.id]);
    }
    
    // Remove from local state
    setItems(items.filter((_, i) => i !== index));
  };

  const handleEditItem = (index: number) => {
    setEditingItem({ item: items[index], index });
  };

  const handleUpdateItem = async (updatedItem: Omit<RegistryItem, 'id' | 'registry_id' | 'is_claimed' | 'claimed_by' | 'created_at' | 'collected_amount'>) => {
    if (editingItem === null) return;

    const { index, item } = editingItem;

    if (index !== null) {
      // Updating existing item
      const newItems = [...items];
      newItems[index] = { ...items[index], ...updatedItem };
      setItems(newItems);
    } else {
      // Adding new item
      try {
        const { data, error: insertError } = await supabase
          .from('registry_items')
          .insert([{
            ...updatedItem,
            registry_id: registry.id
          }])
          .select()
          .single();

        if (insertError) throw insertError;
        if (data) {
          setItems([...items, data]);
        }
      } catch (err) {
        console.error('Error adding new item:', err);
        setError('Failed to add new item');
      }
    }

    setEditingItem(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (loading) return;

    try {
      setLoading(true);
      setError('');

      // Update registry details
      const { error: registryError } = await supabase
        .from('registries')
        .update({
          title,
          description,
          event_date: eventDate,
          thank_you_message: thankYouMessage
        })
        .eq('id', registry.id);

      if (registryError) throw registryError;

      // Delete items marked for deletion
      if (itemsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('registry_items')
          .delete()
          .in('id', itemsToDelete);

        if (deleteError) throw deleteError;
      }

      // Update existing items
      for (const item of items) {
        if (item.id && !itemsToDelete.includes(item.id)) {
          const { error: updateError } = await supabase
            .from('registry_items')
            .update({
              name: item.name,
              price: item.price,
              url: item.url,
              priority: item.priority,
              allows_group_gifting: item.allows_group_gifting,
              target_amount: item.target_amount
            })
            .eq('id', item.id);

          if (updateError) throw updateError;
        }
      }

      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating registry:', err);
      setError('Failed to update registry');
      setLoading(false);
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && !loading && onClose()}
    >
      <div 
        ref={modalRef}
        className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Edit Registry</h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-900/50 text-red-200 p-3 rounded-md">
            {error}
          </div>
        )}

        <form 
          ref={formRef}
          onSubmit={handleSubmit} 
          className="space-y-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                required
              />
            </div>

            <div>
              <DatePicker
                label="Event Date"
                value={eventDate}
                onChange={setEventDate}
                required={true}
                placeholder="Select event date"
              />
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                rows={3}
              />
            </div>
          </div>

          <div className="border-t border-gray-700 pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">Gift Items</h2>
              <button
                type="button"
                onClick={handleAddNewItem}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Add Item</span>
              </button>
            </div>

            <div className="mt-4 space-y-2">
              {items.map((item, index) => (
                <div
                  key={item.id || index}
                  className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg"
                >
                  <div className="flex-1">
                    <h3 className="font-medium text-white">{item.name}</h3>
                    <div className="flex items-center space-x-4">
                      <p className="text-sm text-gray-300">{registry.currency} {item.price}</p>
                      {item.allows_group_gifting && (
                        <p className="text-sm text-gray-300">
                          Target: {registry.currency} {item.target_amount}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`text-sm px-2 py-1 rounded ${
                      item.priority === 'high' ? 'bg-red-900/50 text-red-200' :
                      item.priority === 'medium' ? 'bg-yellow-900/50 text-yellow-200' :
                      'bg-green-900/50 text-green-200'
                    }`}>
                      {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleEditItem(index)}
                      className="text-gray-300 hover:text-white"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Thank You Message
            </label>
            <textarea
              value={thankYouMessage}
              onChange={(e) => setThankYouMessage(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
              rows={3}
              placeholder="Customize the thank you message shown after someone claims a gift..."
            />
            <p className="text-xs text-gray-400 mt-1">
              This message will be shown to guests after they claim a gift from your registry.
            </p>
          </div>

          <div className="border-t border-gray-700 pt-6">
            <GuestListManager registryId={registry.id} />
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-700">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-gray-300 hover:text-white disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {editingItem && (
        <EditItemModal
          item={editingItem.item as Required<Pick<RegistryItem, 'name' | 'price' | 'priority' | 'allows_group_gifting'>> & Partial<RegistryItem>}
          currency={registry.currency}
          onClose={() => setEditingItem(null)}
          onUpdate={handleUpdateItem}
        />
      )}
    </div>
  );
}